/**
 * Global drawer controller.
 *
 * Any detail panel can call `openCrmChatDrawer({ ... })` to surface Ask Type+
 * in a docked right side panel (desktop) with a prefilled record + optional
 * starter prompt. The controller mounts a single `<CrmChatDrawer>` near the
 * app root so callers don't need portals or context plumbing.
 */
import React, { useEffect, useState } from "react";

import CrmChatDrawer from "./components/CrmChatDrawer";
import type {
  AskPageContext,
  CrmChatRecordContext,
  CrmRecordType,
} from "./types/chat.types";

export const CRM_CHAT_DRAWER_OPEN_EVENT = "crm-chat-drawer:open";
export const CRM_CHAT_DRAWER_CLOSE_EVENT = "crm-chat-drawer:close";

export interface OpenCrmChatDrawerOptions {
  workspaceId: string;
  recordId?: string;
  recordType?: CrmRecordType;
  displayName?: string;
  subtitle?: string;
  avatarUrl?: string;
  promptType?: string;
  /** Ask Type anything page/surface context (story 12.1). */
  pageContext?: AskPageContext;
}

interface DrawerState {
  isOpen: boolean;
  workspaceId: string | null;
  record: CrmChatRecordContext | null;
  pageContext: AskPageContext | null;
  promptType: string | null;
}

function recordFromOpenOptions(
  detail: OpenCrmChatDrawerOptions
): CrmChatRecordContext | null {
  if (detail.recordId && detail.recordType) {
    return {
      recordId: detail.recordId,
      recordType: detail.recordType,
      displayName: detail.displayName ?? detail.recordId,
      subtitle: detail.subtitle,
      avatarUrl: detail.avatarUrl,
    };
  }
  if (detail.pageContext?.kind === "crm-record") {
    const ctx = detail.pageContext;
    return {
      recordId: ctx.recordId,
      recordType: (ctx.recordType as CrmRecordType) || "SALE",
      displayName: ctx.displayName ?? ctx.recordId,
      subtitle: ctx.subtitle,
      avatarUrl: ctx.avatarUrl,
    };
  }
  return null;
}

/** Imperative API for opening the chat drawer from anywhere in the app. */
export function openCrmChatDrawer(options: OpenCrmChatDrawerOptions): void {
  if (typeof window === "undefined") return;
  window.dispatchEvent(
    new CustomEvent<OpenCrmChatDrawerOptions>(CRM_CHAT_DRAWER_OPEN_EVENT, { detail: options })
  );
}

/** Imperative API for closing the chat drawer. */
export function closeCrmChatDrawer(): void {
  if (typeof window === "undefined") return;
  window.dispatchEvent(new CustomEvent(CRM_CHAT_DRAWER_CLOSE_EVENT));
}

const CrmChatController: React.FC = () => {
  const [state, setState] = useState<DrawerState>({
    isOpen: false,
    workspaceId: null,
    record: null,
    pageContext: null,
    promptType: null,
  });

  useEffect(() => {
    const handleOpen = (e: Event) => {
      const detail = (e as CustomEvent<OpenCrmChatDrawerOptions>).detail;
      if (!detail || !detail.workspaceId) return;

      setState({
        isOpen: true,
        workspaceId: detail.workspaceId,
        record: recordFromOpenOptions(detail),
        pageContext: detail.pageContext ?? null,
        promptType: detail.promptType ?? null,
      });
    };

    const handleClose = () => {
      setState((prev) => ({ ...prev, isOpen: false }));
    };

    window.addEventListener(CRM_CHAT_DRAWER_OPEN_EVENT, handleOpen);
    window.addEventListener(CRM_CHAT_DRAWER_CLOSE_EVENT, handleClose);
    return () => {
      window.removeEventListener(CRM_CHAT_DRAWER_OPEN_EVENT, handleOpen);
      window.removeEventListener(CRM_CHAT_DRAWER_CLOSE_EVENT, handleClose);
    };
  }, []);

  if (!state.workspaceId) return null;

  return (
    <CrmChatDrawer
      isOpen={state.isOpen}
      onClose={closeCrmChatDrawer}
      workspaceId={state.workspaceId}
      initialRecord={state.record ?? undefined}
      initialPageContext={state.pageContext ?? undefined}
      initialPromptType={state.promptType ?? undefined}
    />
  );
};

export default CrmChatController;
